import { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Truck,
  Search,
  Filter,
  Wifi,
  WifiOff,
  ArrowRightLeft,
  Building2,
  X,
  Car,
  Bike,
  Bus,
  Ship,
  Plane,
  Upload,
  FileText,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Send,
  AlertTriangle,
  Radio,
  ChevronDown,
  ChevronUp,
  Power,
  PowerOff,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import clsx from 'clsx';
import TeltonikaCommandPicker from '../../components/TeltonikaCommandPicker';
import type { TeltonikaCommand } from '../../data/teltonika-commands';

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface DeviceGroup {
  id: string;
  name: string;
  color: string;
}

interface Device {
  id: string;
  imei: string;
  name: string;
  plate?: string;
  brand?: string;
  model?: string;
  year?: number;
  vin?: string;
  trackerModel?: string;
  simNumber?: string;
  simIccid?: string;
  simType?: string;
  category?: string;
  icon?: string;
  markerColor?: string;
  speedLimit?: number;
  fuelCapacity?: number;
  tag?: string;
  isActive?: boolean;
  status: 'ONLINE' | 'OFFLINE' | 'UNKNOWN';
  lastPositionAt?: string;
  tenant: Tenant;
  group?: DeviceGroup;
}

interface TruphoneScanResult {
  newDevices: Array<{ iccid: string; msisdn: string; imei: string; trackerModel: string; simLabel: string; tag: string }>;
  existingDevices: Array<{ iccid: string; imei: string; deviceName: string; tenantName: string }>;
  noResponse: Array<{ iccid: string; msisdn: string; simLabel: string }>;
  totalActive: number;
}

interface TruphoneImportDevice {
  iccid: string;
  msisdn: string;
  imei: string;
  trackerModel: string;
  simLabel: string;
  tag: string;
  name: string;
  selected: boolean;
}

interface TruphoneImportResult {
  created: number;
  skipped: number;
  errors: Array<{ imei: string; error: string }>;
}

const VEHICLE_ICONS = [
  { value: 'truck', label: 'Camion', icon: Truck },
  { value: 'car', label: 'Voiture', icon: Car },
  { value: 'motorcycle', label: 'Moto', icon: Bike },
  { value: 'bus', label: 'Bus', icon: Bus },
  { value: 'van', label: 'Utilitaire', icon: Truck },
  { value: 'boat', label: 'Bateau', icon: Ship },
  { value: 'plane', label: 'Avion', icon: Plane },
];

const MARKER_COLORS = [
  { value: '#10b981', label: 'Vert' },
  { value: '#3b82f6', label: 'Bleu' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#f59e0b', label: 'Orange' },
  { value: '#ef4444', label: 'Rouge' },
  { value: '#ec4899', label: 'Rose' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#64748b', label: 'Gris' },
];

// Teltonika FM series tracker models
const teltonikaTrackerModels = [
  // FMB Series - Basic
  { value: 'FMB001', label: 'FMB001 - OBD Tracker', category: 'OBD', features: ['OBD-II', '2G'] },
  { value: 'FMB002', label: 'FMB002 - OBD Tracker', category: 'OBD', features: ['OBD-II', '2G'] },
  { value: 'FMB003', label: 'FMB003 - OBD Tracker Advanced', category: 'OBD', features: ['OBD-II', 'OEM', '2G'] },
  { value: 'FMB010', label: 'FMB010 - OBD Tracker', category: 'OBD', features: ['OBD-II', '2G'] },
  { value: 'FMB020', label: 'FMB020 - OBD Eco Tracker', category: 'OBD', features: ['OBD-II', 'Eco-Driving', '2G'] },

  // FMB Series - Professional
  { value: 'FMB110', label: 'FMB110 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN'] },
  { value: 'FMB120', label: 'FMB120 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN', 'Bluetooth'] },
  { value: 'FMB122', label: 'FMB122 - Professional Tracker', category: 'Professional', features: ['2G', 'CAN', 'Bluetooth'] },
  { value: 'FMB125', label: 'FMB125 - Professional Tracker', category: 'Professional', features: ['2G', 'CAN', 'Bluetooth'] },
  { value: 'FMB130', label: 'FMB130 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN'] },
  { value: 'FMB140', label: 'FMB140 - Advanced Tracker', category: 'Professional', features: ['2G', 'CAN'] },

  // FMB Series - Easy
  { value: 'FMB202', label: 'FMB202 - Waterproof Tracker', category: 'Easy', features: ['2G', 'IP67'] },
  { value: 'FMB204', label: 'FMB204 - Motorcycle Tracker', category: 'Easy', features: ['2G', 'IP67', 'Moto'] },
  { value: 'FMB208', label: 'FMB208 - Compact Tracker', category: 'Easy', features: ['2G', 'Compact'] },
  { value: 'FMB209', label: 'FMB209 - Hidden Tracker', category: 'Easy', features: ['2G', 'Hidden'] },

  // FMB Series - GNSS
  { value: 'FMB900', label: 'FMB900 - Entry Level', category: 'GNSS', features: ['2G', 'Basic'] },
  { value: 'FMB910', label: 'FMB910 - Smart Tracker', category: 'GNSS', features: ['2G', 'Bluetooth'] },
  { value: 'FMB920', label: 'FMB920 - Smart Tracker', category: 'GNSS', features: ['2G', 'Bluetooth', 'CAN'] },
  { value: 'FMB930', label: 'FMB930 - Advanced Tracker', category: 'GNSS', features: ['2G', 'RS232/RS485'] },
  { value: 'FMB962', label: 'FMB962 - OBDII Tracker', category: 'GNSS', features: ['2G', 'OBD-II', 'CAN'] },
  { value: 'FMB964', label: 'FMB964 - OBDII Tracker', category: 'GNSS', features: ['2G', 'OBD-II', 'CAN'] },

  // FMC Series - 4G/LTE
  { value: 'FMC001', label: 'FMC001 - 4G OBD Tracker', category: '4G OBD', features: ['OBD-II', '4G LTE'] },
  { value: 'FMC003', label: 'FMC003 - 4G OBD Advanced', category: '4G OBD', features: ['OBD-II', 'OEM', '4G LTE'] },
  { value: 'FMC125', label: 'FMC125 - 4G Professional', category: '4G Pro', features: ['4G LTE', 'CAN', 'Bluetooth'] },
  { value: 'FMC130', label: 'FMC130 - 4G Advanced', category: '4G Pro', features: ['4G LTE', 'CAN'] },
  { value: 'FMC150', label: 'FMC150 - 4G LTE Cat1', category: '4G Pro', features: ['4G LTE Cat1', 'CAN'] },
  { value: 'FMC230', label: 'FMC230 - 4G LTE Cat M1', category: '4G Pro', features: ['4G LTE Cat M1', 'CAN'] },
  { value: 'FMC640', label: 'FMC640 - 4G Advanced', category: '4G Pro', features: ['4G LTE', 'CAN', 'RS232'] },
  { value: 'FMC650', label: 'FMC650 - 4G HD Camera', category: '4G Pro', features: ['4G LTE', 'Camera', 'ADAS'] },
  { value: 'FMC880', label: 'FMC880 - 4G Fleet', category: '4G Pro', features: ['4G LTE', 'CAN FMS'] },

  // FMM Series - LTE Cat M1
  { value: 'FMM001', label: 'FMM001 - Cat M1 OBD', category: 'Cat M1', features: ['OBD-II', 'LTE Cat M1'] },
  { value: 'FMM003', label: 'FMM003 - Cat M1 OBD Advanced', category: 'Cat M1', features: ['OBD-II', 'OEM', 'LTE Cat M1'] },
  { value: 'FMM125', label: 'FMM125 - Cat M1 Professional', category: 'Cat M1', features: ['LTE Cat M1', 'CAN'] },
  { value: 'FMM130', label: 'FMM130 - Cat M1 Advanced', category: 'Cat M1', features: ['LTE Cat M1', 'CAN'] },
  { value: 'FMM230', label: 'FMM230 - Cat M1 Advanced', category: 'Cat M1', features: ['LTE Cat M1', 'CAN'] },
  { value: 'FMM640', label: 'FMM640 - Cat M1 Fleet', category: 'Cat M1', features: ['LTE Cat M1', 'RS232'] },

  // Asset Trackers
  { value: 'TAT100', label: 'TAT100 - Asset Tracker', category: 'Asset', features: ['2G', 'Battery', 'IP67'] },
  { value: 'TAT140', label: 'TAT140 - 4G Asset Tracker', category: 'Asset', features: ['4G LTE', 'Battery', 'IP67'] },
  { value: 'TAT240', label: 'TAT240 - Cat M1 Asset', category: 'Asset', features: ['LTE Cat M1', 'Battery'] },

  // Other
  { value: 'OTHER', label: 'Autre modèle', category: 'Other', features: [] },
];

export default function DevicesAdminPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<Device | null>(null);
  const [transferringDevice, setTransferringDevice] = useState<Device | null>(null);
  const [transferTargetId, setTransferTargetId] = useState('');

  // SMS state
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsDevice, setSmsDevice] = useState<Device | null>(null);
  const [smsCommand, setSmsCommand] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsResult, setSmsResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedSmsCmd, setSelectedSmsCmd] = useState<TeltonikaCommand | null>(null);
  const [selectedBulkSmsCmd, setSelectedBulkSmsCmd] = useState<TeltonikaCommand | null>(null);

  // Import state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importTenantId, setImportTenantId] = useState('');
  const [importData, setImportData] = useState<Array<Record<string, string>>>([]);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: Array<{ imei: string; error: string }>;
  } | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  // Bulk delete state
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Bulk transfer state
  const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
  const [bulkTransferTenantId, setBulkTransferTenantId] = useState('');
  const [bulkTransferLoading, setBulkTransferLoading] = useState(false);

  // Bulk SMS state
  const [showBulkSmsModal, setShowBulkSmsModal] = useState(false);
  const [bulkSmsCommand, setBulkSmsCommand] = useState('');
  const [bulkSmsMessage, setBulkSmsMessage] = useState('');
  const [bulkSmsLoading, setBulkSmsLoading] = useState(false);
  const [bulkSmsResult, setBulkSmsResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // FOTA Web push config state
  const [showFotaModal, setShowFotaModal] = useState(false);
  const [fotaCfgFile, setFotaCfgFile] = useState<File | null>(null);
  const [fotaCfgBase64, setFotaCfgBase64] = useState('');
  const [fotaLoading, setFotaLoading] = useState(false);
  const [fotaResult, setFotaResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Truphone import state
  const [showTruphoneImportModal, setShowTruphoneImportModal] = useState(false);
  const [truphoneImportStep, setTruphoneImportStep] = useState<'config' | 'sending' | 'waiting' | 'collecting' | 'results' | 'creating' | 'done'>('config');
  const [truphoneImportTenantId, setTruphoneImportTenantId] = useState('');
  const [truphoneSendResult, setTruphoneSendResult] = useState<{ sent: number; skipped: number; totalActive: number } | null>(null);
  const [truphoneScanResult, setTruphoneScanResult] = useState<TruphoneScanResult | null>(null);
  const [truphoneImportDevices, setTruphoneImportDevices] = useState<TruphoneImportDevice[]>([]);
  const [truphoneImportResult, setTruphoneImportResult] = useState<TruphoneImportResult | null>(null);
  const [truphoneShowExisting, setTruphoneShowExisting] = useState(false);
  const [truphoneShowNoResponse, setTruphoneShowNoResponse] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterTenantId, setFilterTenantId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [formData, setFormData] = useState({
    tenantId: '',
    trackerType: 'HARDWARE' as 'HARDWARE' | 'OEM',
    imei: '',
    name: '',
    plate: '',
    brand: '',
    model: '',
    year: '',
    vin: '',
    trackerModel: '',
    simNumber: '',
    simIccid: '',
    simType: '',
    category: '',
    icon: 'truck',
    markerColor: '#10b981',
    speedLimit: '90',
    fuelCapacity: '',
    isActive: true,
  });

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    loadDevices();
  }, [page, filterTenantId, filterStatus, sortBy, sortOrder]);

  const loadTenants = async () => {
    try {
      const res = await adminApi.getTenants({ limit: 1000 });
      const list = res.data?.data || [];
      list.sort((a: Tenant, b: Tenant) => a.name.localeCompare(b.name));
      setTenants(list);
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  };

  const loadDevices = async () => {
    setLoading(true);
    setSelectedDevices(new Set());
    try {
      const res = await adminApi.getDevices({
        page,
        limit: 50,
        tenantId: filterTenantId || undefined,
        status: filterStatus || undefined,
        search: search || undefined,
        sortBy,
        sortOrder,
      });
      setDevices(res.data?.data || []);
      setTotalPages(res.data?.pagination?.totalPages || 1);
      setTotal(res.data?.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadDevices();
  };

  const filteredDevices = useMemo(() => {
    if (!search) return devices;
    const s = search.toLowerCase();
    return devices.filter(
      (d) =>
        d.name.toLowerCase().includes(s) ||
        d.imei.includes(s) ||
        d.plate?.toLowerCase().includes(s) ||
        d.tenant.name.toLowerCase().includes(s) ||
        d.tag?.toLowerCase().includes(s)
    );
  }, [devices, search]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminApi.createDevice({
        tenantId: formData.tenantId,
        oemProvider: formData.trackerType === 'OEM' ? 'HIGH_MOBILITY' : null,
        imei: formData.trackerType === 'OEM' ? undefined : formData.imei,
        name: formData.name,
        plate: formData.plate || null,
        brand: formData.brand || null,
        model: formData.model || null,
        year: formData.year ? parseInt(formData.year) : null,
        vin: formData.vin || null,
        trackerModel: formData.trackerModel || null,
        simNumber: formData.simNumber || null,
        simIccid: formData.simIccid || null,
        simType: formData.simType || null,
        category: formData.category || null,
        icon: formData.icon || 'truck',
        markerColor: formData.markerColor || '#10b981',
        speedLimit: formData.speedLimit ? parseInt(formData.speedLimit) : null,
        fuelCapacity: formData.fuelCapacity ? parseFloat(formData.fuelCapacity) : null,
        isActive: formData.isActive,
      });
      setShowModal(false);
      resetForm();
      loadDevices();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDevice) return;
    try {
      await adminApi.updateDevice(editingDevice.id, {
        name: formData.name,
        plate: formData.plate || null,
        brand: formData.brand || null,
        model: formData.model || null,
        year: formData.year ? parseInt(formData.year) : null,
        vin: formData.vin || null,
        trackerModel: formData.trackerModel || null,
        simNumber: formData.simNumber || null,
        simIccid: formData.simIccid || null,
        simType: formData.simType || null,
        category: formData.category || null,
        icon: formData.icon || 'truck',
        markerColor: formData.markerColor || '#10b981',
        speedLimit: formData.speedLimit ? parseInt(formData.speedLimit) : null,
        fuelCapacity: formData.fuelCapacity ? parseFloat(formData.fuelCapacity) : null,
        tenantId: formData.tenantId !== editingDevice.tenant.id ? formData.tenantId : undefined,
        isActive: formData.isActive,
      });
      setShowModal(false);
      setEditingDevice(null);
      resetForm();
      loadDevices();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!deletingDevice) return;
    try {
      await adminApi.deleteDevice(deletingDevice.id);
      setShowDeleteModal(false);
      setDeletingDevice(null);
      loadDevices();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleTransfer = async () => {
    if (!transferringDevice || !transferTargetId) return;
    try {
      await adminApi.transferDevice(transferringDevice.id, transferTargetId);
      setShowTransferModal(false);
      setTransferringDevice(null);
      setTransferTargetId('');
      loadDevices();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur lors du transfert');
    }
  };

  const handleImport = async () => {
    if (!importTenantId || importData.length === 0) return;
    setImportLoading(true);
    try {
      const res = await adminApi.importDevices(importTenantId, importData as any);
      setImportResult({
        imported: res.data.imported,
        skipped: res.data.skipped,
        errors: res.data.errors,
      });
      loadDevices();
    } catch {
      setImportResult({
        imported: 0,
        skipped: importData.length,
        errors: [{ imei: 'N/A', error: "Erreur lors de l'import" }],
      });
    } finally {
      setImportLoading(false);
    }
  };

  const openEditModal = (device: Device) => {
    setEditingDevice(device);
    setFormData({
      tenantId: device.tenant.id,
      trackerType: (device as { oemProvider?: string | null }).oemProvider ? 'OEM' : 'HARDWARE',
      imei: device.imei,
      name: device.name,
      plate: device.plate || '',
      brand: device.brand || '',
      model: device.model || '',
      year: device.year?.toString() || '',
      vin: device.vin || '',
      trackerModel: device.trackerModel || '',
      simNumber: device.simNumber || '',
      simIccid: device.simIccid || '',
      simType: device.simType || '',
      category: device.category || '',
      icon: device.icon || 'truck',
      markerColor: device.markerColor || '#10b981',
      speedLimit: device.speedLimit?.toString() || '90',
      fuelCapacity: device.fuelCapacity?.toString() || '',
      isActive: device.isActive !== false,
    });
    setShowModal(true);
  };

  const openTransferModal = (device: Device) => {
    setTransferringDevice(device);
    setTransferTargetId('');
    setShowTransferModal(true);
  };

  const resetForm = () => {
    setFormData({
      tenantId: '',
      trackerType: 'HARDWARE',
      imei: '',
      name: '',
      plate: '',
      brand: '',
      model: '',
      year: '',
      vin: '',
      trackerModel: '',
      simNumber: '',
      simIccid: '',
      simType: '',
      category: '',
      icon: 'truck',
      markerColor: '#10b981',
      speedLimit: '90',
      fuelCapacity: '',
      isActive: true,
    });
  };

  const openSmsModal = (device: Device) => {
    setSmsDevice(device);
    setSmsCommand('');
    setSmsMessage('');
    setSmsResult(null);
    setShowSmsModal(true);
  };

  const handleSmsCommandChange = (command: string, cmd?: TeltonikaCommand | null) => {
    setSmsCommand(command);
    setSelectedSmsCmd(cmd || null);
    if (command === 'custom') {
      setSmsMessage('');
    } else if (cmd?.needsValue) {
      // For setparam commands, pre-fill the base command, user adds value
      setSmsMessage(command);
    } else {
      setSmsMessage(command);
    }
  };

  const handleSendSms = async () => {
    if (!smsDevice || !smsMessage) return;
    if (!smsDevice.simIccid) {
      setSmsResult({ type: 'error', text: 'Ce véhicule n\'a pas d\'ICCID SIM configuré. Ajoutez-le dans les paramètres du véhicule.' });
      return;
    }
    setSmsLoading(true);
    setSmsResult(null);

    try {
      await adminApi.sendTruphoneSms({
        deviceId: smsDevice.id,
        iccid: smsDevice.simIccid,
        command: smsMessage,
      });
      setSmsResult({ type: 'success', text: 'SMS envoyé avec succès' });
    } catch (error: any) {
      setSmsResult({
        type: 'error',
        text: error.response?.data?.error || 'Erreur lors de l\'envoi du SMS',
      });
    } finally {
      setSmsLoading(false);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      await adminApi.bulkDeleteDevices(Array.from(selectedDevices));
      setShowBulkDeleteModal(false);
      setSelectedDevices(new Set());
      loadDevices();
    } catch (error) {
      console.error('Error bulk deleting devices:', error);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Bulk transfer handler
  const handleBulkTransfer = async () => {
    if (!bulkTransferTenantId) return;
    setBulkTransferLoading(true);
    try {
      const res = await adminApi.bulkTransferDevices(Array.from(selectedDevices), bulkTransferTenantId);
      setShowBulkTransferModal(false);
      setSelectedDevices(new Set());
      loadDevices();
      alert(`${res.data.transferred} véhicule(s) transféré(s) vers "${res.data.targetTenant}"`);
    } catch (error: any) {
      console.error('Error bulk transferring devices:', error);
      alert(error.response?.data?.error || 'Erreur lors du transfert');
    } finally {
      setBulkTransferLoading(false);
    }
  };

  // Bulk SMS handler
  const handleBulkSms = async () => {
    if (!bulkSmsMessage) return;
    setBulkSmsLoading(true);
    setBulkSmsResult(null);
    try {
      const res = await adminApi.bulkSms(Array.from(selectedDevices), bulkSmsMessage);
      const data = res.data;
      setBulkSmsResult({
        type: 'success',
        text: `SMS envoyé à ${data.sent} véhicule${data.sent > 1 ? 's' : ''}${data.skipped > 0 ? ` (${data.skipped} ignoré${data.skipped > 1 ? 's' : ''} - pas de SIM Truphone)` : ''}`,
      });
    } catch (error: any) {
      setBulkSmsResult({
        type: 'error',
        text: error.response?.data?.error || 'Erreur lors de l\'envoi groupé',
      });
    } finally {
      setBulkSmsLoading(false);
    }
  };

  // FOTA Web push config handler
  const handleFotaCfgSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotaCfgFile(file);
    setFotaResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1] || '';
      setFotaCfgBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleFotaPush = async () => {
    if (!fotaCfgBase64 || !fotaCfgFile) return;
    setFotaLoading(true);
    setFotaResult(null);

    // Get IMEIs from selected devices
    const selectedImeis = Array.from(selectedDevices)
      .map((id) => devices.find((d) => d.id === id)?.imei)
      .filter((imei): imei is string => !!imei);

    if (selectedImeis.length === 0) {
      setFotaResult({ type: 'error', text: 'Aucun IMEI trouvé pour les véhicules sélectionnés' });
      setFotaLoading(false);
      return;
    }

    try {
      const res = await adminApi.pushFotaWebConfig({
        imeis: selectedImeis,
        cfgBase64: fotaCfgBase64,
        filename: fotaCfgFile.name,
      });
      const { taskCount, failedCount } = res.data;
      let text = `Configuration envoyée pour ${taskCount} véhicule${taskCount > 1 ? 's' : ''}.`;
      if (failedCount > 0) {
        text += ` ${failedCount} échec${failedCount > 1 ? 's' : ''}.`;
      }
      text += ' Les trackers recevront la config à leur prochain check-in FOTA.';
      setFotaResult({ type: failedCount > 0 && taskCount === 0 ? 'error' : 'success', text });
    } catch (error: any) {
      setFotaResult({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.details || 'Erreur lors de l\'envoi de la configuration',
      });
    } finally {
      setFotaLoading(false);
    }
  };

  // Bulk toggle active handler
  const handleBulkToggleActive = async (isActive: boolean) => {
    try {
      const res = await adminApi.bulkToggleActiveDevices(Array.from(selectedDevices), isActive);
      setSelectedDevices(new Set());
      loadDevices();
      alert(`${res.data.updated} véhicule(s) ${isActive ? 'activé(s)' : 'désactivé(s)'}`);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleBulkSmsCommandChange = (command: string, cmd?: TeltonikaCommand | null) => {
    setBulkSmsCommand(command);
    setSelectedBulkSmsCmd(cmd || null);
    if (command === 'custom') {
      setBulkSmsMessage('');
    } else if (cmd?.needsValue) {
      setBulkSmsMessage(command);
    } else {
      setBulkSmsMessage(command);
    }
  };

  // Truphone import: send getver to all active SIMs
  const handleTruphoneSend = async () => {
    setTruphoneImportStep('sending');
    try {
      const res = await adminApi.truphoneImportSend();
      setTruphoneSendResult({ sent: res.data.sent, skipped: res.data.skipped, totalActive: res.data.totalActive });
      setTruphoneImportStep('waiting');
    } catch (error: any) {
      console.error('Truphone send error:', error);
      setTruphoneImportStep('config');
      alert(error.response?.data?.error || 'Erreur lors de l\'envoi getver');
    }
  };

  // Truphone import: collect responses from SMS history
  const handleTruphoneCollect = async () => {
    setTruphoneImportStep('collecting');
    try {
      const res = await adminApi.truphoneImportCollect();
      const data = res.data;
      setTruphoneScanResult(data);
      setTruphoneImportDevices(
        data.newDevices.map((d) => ({
          ...d,
          name: d.simLabel || `Tracker-${d.imei.slice(-4)}`,
          selected: true,
        }))
      );
      setTruphoneImportStep('results');
    } catch (error: any) {
      console.error('Truphone collect error:', error);
      setTruphoneImportStep('waiting');
      alert(error.response?.data?.error || 'Erreur lors de la collecte des réponses');
    }
  };

  // Truphone import: create devices
  const handleTruphoneImportCreate = async () => {
    const selectedItems = truphoneImportDevices.filter((d) => d.selected);
    if (selectedItems.length === 0) return;
    setTruphoneImportStep('creating');
    try {
      const res = await adminApi.truphoneImportCreate({
        tenantId: truphoneImportTenantId,
        devices: selectedItems.map((d) => ({
          iccid: d.iccid,
          imei: d.imei,
          trackerModel: d.trackerModel,
          name: d.name,
          msisdn: d.msisdn,
          tag: d.tag || undefined,
        })),
      });
      setTruphoneImportResult(res.data);
      setTruphoneImportStep('done');
    } catch (error: any) {
      console.error('Truphone import create error:', error);
      setTruphoneImportStep('results');
      alert(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  // Check if all selected devices have Truphone SIM (for bulk SMS)
  const allSelectedAreTruphone = useMemo(() => {
    if (selectedDevices.size === 0) return false;
    return Array.from(selectedDevices).every((id) => {
      const device = devices.find((d) => d.id === id);
      return device?.simType?.toLowerCase() === 'truphone' && device?.simIccid;
    });
  }, [selectedDevices, devices]);

  // Toggle select all (checkbox header)
  const toggleSelectAll = () => {
    if (selectedDevices.size === filteredDevices.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(filteredDevices.map((d) => d.id)));
    }
  };

  const toggleSelectDevice = (id: string) => {
    setSelectedDevices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ONLINE':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Wifi className="h-3 w-3" /> En ligne
          </span>
        );
      case 'OFFLINE':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            <WifiOff className="h-3 w-3" /> Hors ligne
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            Inconnu
          </span>
        );
    }
  };

  if (loading && devices.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Véhicules</h1>
          <p className="text-gray-500">
            {total} véhicule{total > 1 ? 's' : ''} sur tous les tenants
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedDevices.size > 0 && (
            <>
              <button
                onClick={() => handleBulkToggleActive(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                <Power className="h-4 w-4" />
                Activer ({selectedDevices.size})
              </button>
              <button
                onClick={() => handleBulkToggleActive(false)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <PowerOff className="h-4 w-4" />
                Désactiver ({selectedDevices.size})
              </button>
              <button
                onClick={() => {
                  setShowBulkSmsModal(true);
                  setBulkSmsCommand('');
                  setBulkSmsMessage('');
                  setBulkSmsResult(null);
                }}
                disabled={!allSelectedAreTruphone}
                title={!allSelectedAreTruphone ? 'Tous les véhicules sélectionnés doivent avoir une SIM Truphone' : ''}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${allSelectedAreTruphone ? 'bg-cyan-600 text-white hover:bg-cyan-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              >
                <MessageSquare className="h-4 w-4" />
                SMS ({selectedDevices.size})
              </button>
              <button
                onClick={() => {
                  setShowFotaModal(true);
                  setFotaCfgFile(null);
                  setFotaCfgBase64('');
                  setFotaResult(null);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
              >
                <Upload className="h-4 w-4" />
                FOTA Config ({selectedDevices.size})
              </button>
              <button
                onClick={() => { setShowBulkTransferModal(true); setBulkTransferTenantId(''); }}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Transférer ({selectedDevices.size})
              </button>
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Supprimer ({selectedDevices.size})
              </button>
            </>
          )}
          <button
            onClick={() => {
              setShowTruphoneImportModal(true);
              setTruphoneImportStep('config');
              setTruphoneImportTenantId('');
              setTruphoneSendResult(null);
              setTruphoneScanResult(null);
              setTruphoneImportDevices([]);
              setTruphoneImportResult(null);
              setTruphoneShowExisting(false);
              setTruphoneShowNoResponse(false);
            }}
            className="btn-secondary flex items-center gap-2"
          >
            <Radio className="h-5 w-5" />
            Importer Truphone
          </button>
          <button
            onClick={() => {
              setImportData([]);
              setImportResult(null);
              setImportTenantId('');
              setIsImportModalOpen(true);
            }}
            className="btn-secondary flex items-center gap-2"
          >
            <Upload className="h-5 w-5" />
            Importer CSV
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingDevice(null);
              setShowModal(true);
            }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Nouveau Véhicule
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher (nom, IMEI, plaque, tenant, tag)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Tenant filter */}
          <div className="w-48">
            <select
              value={filterTenantId}
              onChange={(e) => {
                setFilterTenantId(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500"
            >
              <option value="">Tous les tenants</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <div className="w-40">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500"
            >
              <option value="">Tous les statuts</option>
              <option value="ONLINE">En ligne</option>
              <option value="OFFLINE">Hors ligne</option>
              <option value="UNKNOWN">Inconnu</option>
              <option value="ACTIVE">Actif</option>
              <option value="INACTIVE">Inactif</option>
            </select>
          </div>

          <button onClick={handleSearch} className="btn-secondary">
            <Filter className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Devices Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filteredDevices.length > 0 && selectedDevices.size === filteredDevices.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'name') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('name'); setSortOrder('asc'); } setPage(1); }}>
                  Véhicule {sortBy === 'name' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'tenant') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('tenant'); setSortOrder('asc'); } setPage(1); }}>
                  Tenant {sortBy === 'tenant' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'imei') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('imei'); setSortOrder('asc'); } setPage(1); }}>
                  IMEI {sortBy === 'imei' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'trackerModel') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('trackerModel'); setSortOrder('asc'); } setPage(1); }}>
                  Tracker {sortBy === 'trackerModel' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'tag') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('tag'); setSortOrder('asc'); } setPage(1); }}>
                  Tag {sortBy === 'tag' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'status') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('status'); setSortOrder('asc'); } setPage(1); }}>
                  Statut {sortBy === 'status' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredDevices.map((device) => {
                const IconComponent =
                  VEHICLE_ICONS.find((i) => i.value === device.icon)?.icon || Truck;
                return (
                  <tr
                    key={device.id}
                    onClick={() => openEditModal(device)}
                    className={clsx('hover:bg-gray-50 cursor-pointer', selectedDevices.has(device.id) && 'bg-cyan-50')}
                  >
                    <td className="px-2 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedDevices.has(device.id)}
                        onChange={() => toggleSelectDevice(device.id)}
                        className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${device.markerColor || '#10b981'}20` }}
                        >
                          <IconComponent
                            className="h-5 w-5"
                            style={{ color: device.markerColor || '#10b981' }}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{device.name}</span>
                            {device.isActive === false && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Inactif
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {device.plate && <span className="mr-2">{device.plate}</span>}
                            {device.brand && device.model && (
                              <span>
                                {device.brand} {device.model}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900">{device.tenant.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {device.imei}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600">{device.trackerModel || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {device.tag ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {device.tag}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(device.status)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openTransferModal(device)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Transférer vers un autre tenant"
                        >
                          <ArrowRightLeft className="h-4 w-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => openEditModal(device)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Pencil className="h-4 w-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => openSmsModal(device)}
                          disabled={device.simType !== 'Truphone' || !device.simIccid}
                          className={clsx(
                            'p-2 rounded-lg transition-colors',
                            device.simType === 'Truphone' && device.simIccid
                              ? 'hover:bg-cyan-50'
                              : 'opacity-50 cursor-not-allowed'
                          )}
                          title={device.simType === 'Truphone' && device.simIccid ? 'Envoyer SMS' : 'SMS non disponible (SIM Truphone requise)'}
                        >
                          <MessageSquare className="h-4 w-4 text-cyan-500" />
                        </button>
                        <button
                          onClick={() => {
                            setDeletingDevice(device);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredDevices.length === 0 && (
          <div className="p-8 text-center">
            <Truck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucun véhicule trouvé</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (() => {
          const devicesLimit = 50;
          const getPageNumbers = () => {
            const pages: (number | '...')[] = [];
            if (totalPages <= 7) {
              for (let i = 1; i <= totalPages; i++) pages.push(i);
            } else {
              pages.push(1);
              if (page > 3) pages.push('...');
              const start = Math.max(2, page - 1);
              const end = Math.min(totalPages - 1, page + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              if (page < totalPages - 2) pages.push('...');
              pages.push(totalPages);
            }
            return pages;
          };
          return (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {(page - 1) * devicesLimit + 1}-{Math.min(page * devicesLimit, total)} sur {total} véhicules
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Préc.
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-gray-400">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        p === page
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suiv.
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl m-4 my-8">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingDevice ? 'Modifier le Véhicule' : 'Nouveau Véhicule'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingDevice(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={editingDevice ? handleUpdate : handleCreate}>
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Active/Inactive switch */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Statut du véhicule</span>
                    <p className="text-xs text-gray-500">
                      {formData.isActive ? 'Visible par le tenant' : 'Masqué pour le tenant (carte et liste)'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                    className={clsx(
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                      formData.isActive ? 'bg-emerald-500' : 'bg-gray-300'
                    )}
                  >
                    <span
                      className={clsx(
                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                        formData.isActive ? 'translate-x-6' : 'translate-x-1'
                      )}
                    />
                  </button>
                  <span className={clsx('text-sm font-medium ml-2', formData.isActive ? 'text-emerald-600' : 'text-red-600')}>
                    {formData.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>

                {/* Tenant selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant *
                  </label>
                  <select
                    value={formData.tenantId}
                    onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
                    required
                    disabled={!!editingDevice}
                  >
                    <option value="">Sélectionner un tenant...</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tracker type (create only) */}
                {!editingDevice && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de suivi</label>
                    <select
                      value={formData.trackerType}
                      onChange={(e) => setFormData({ ...formData, trackerType: e.target.value as 'HARDWARE' | 'OEM' })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                    >
                      <option value="HARDWARE">Boîtier GPS (IMEI)</option>
                      <option value="OEM">OEM Connect — Données constructeur (VIN)</option>
                    </select>
                  </div>
                )}

                {/* Basic info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    {formData.trackerType === 'OEM' ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">VIN *</label>
                        <input
                          type="text"
                          value={formData.vin}
                          onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase().replace(/\s/g, '') })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 font-mono"
                          required
                          maxLength={17}
                          minLength={17}
                          placeholder="VF1XXXXXXXXXXXXXX"
                          disabled={!!editingDevice}
                        />
                      </>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">IMEI *</label>
                        <input
                          type="text"
                          value={formData.imei}
                          onChange={(e) => setFormData({ ...formData, imei: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                          required
                          maxLength={15}
                          pattern="[0-9]{15}"
                          placeholder="352625090000001"
                          disabled={!!editingDevice}
                        />
                      </>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      required
                      placeholder="Camion 1"
                    />
                  </div>
                </div>

                {/* Vehicle info */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plaque</label>
                    <input
                      type="text"
                      value={formData.plate}
                      onChange={(e) => setFormData({ ...formData, plate: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      placeholder="AB-123-CD"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      placeholder="Renault"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      placeholder="Master"
                    />
                  </div>
                </div>

                {/* Tracker info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Modèle Tracker
                    </label>
                    <select
                      value={formData.trackerModel}
                      onChange={(e) => setFormData({ ...formData, trackerModel: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                    >
                      <option value="">Sélectionner un modèle</option>
                      <optgroup label="OBD Trackers (2G)">
                        {teltonikaTrackerModels.filter(t => t.category === 'OBD').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Professional (2G)">
                        {teltonikaTrackerModels.filter(t => t.category === 'Professional').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="GNSS Trackers (2G)">
                        {teltonikaTrackerModels.filter(t => t.category === 'GNSS').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="4G OBD">
                        {teltonikaTrackerModels.filter(t => t.category === '4G OBD').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="4G Professional">
                        {teltonikaTrackerModels.filter(t => t.category === '4G Pro').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="LTE Cat M1">
                        {teltonikaTrackerModels.filter(t => t.category === 'Cat M1').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Asset Trackers">
                        {teltonikaTrackerModels.filter(t => t.category === 'Asset').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Autres">
                        {teltonikaTrackerModels.filter(t => ['Easy', 'Other'].includes(t.category)).map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                    </select>
                    {formData.trackerModel && formData.trackerModel !== 'OTHER' && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {teltonikaTrackerModels.find(t => t.value === formData.trackerModel)?.features.map((feat, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-700">
                            {feat}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      N° SIM
                    </label>
                    <input
                      type="text"
                      value={formData.simNumber}
                      onChange={(e) => setFormData({ ...formData, simNumber: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      placeholder="+33612345678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ICCID
                    </label>
                    <input
                      type="text"
                      value={formData.simIccid}
                      onChange={(e) => setFormData({ ...formData, simIccid: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      placeholder="8933..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de SIM
                    </label>
                    <select
                      value={formData.simType}
                      onChange={(e) => setFormData({ ...formData, simType: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                    >
                      <option value="">Aucun</option>
                      <option value="Truphone">Truphone</option>
                      <option value="Comgate">Comgate</option>
                    </select>
                  </div>
                </div>

                {/* Icon and color */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
                    <div className="flex flex-wrap gap-2">
                      {VEHICLE_ICONS.map((iconOption) => {
                        const IconComp = iconOption.icon;
                        return (
                          <button
                            key={iconOption.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, icon: iconOption.value })}
                            className={clsx(
                              'p-2 rounded-lg border-2 transition-colors',
                              formData.icon === iconOption.value
                                ? 'border-cyan-500 bg-cyan-50'
                                : 'border-gray-200 hover:border-gray-300'
                            )}
                            title={iconOption.label}
                          >
                            <IconComp className="h-5 w-5 text-gray-600" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                    <div className="flex flex-wrap gap-2">
                      {MARKER_COLORS.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, markerColor: color.value })}
                          className={clsx(
                            'w-8 h-8 rounded-full border-2 transition-transform',
                            formData.markerColor === color.value
                              ? 'border-gray-800 scale-110'
                              : 'border-transparent hover:scale-105'
                          )}
                          style={{ backgroundColor: color.value }}
                          title={color.label}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Speed limit */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Limite vitesse (km/h)
                    </label>
                    <input
                      type="number"
                      value={formData.speedLimit}
                      onChange={(e) => setFormData({ ...formData, speedLimit: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      min={0}
                      max={300}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Capacité carburant (L)
                    </label>
                    <input
                      type="number"
                      value={formData.fuelCapacity}
                      onChange={(e) => setFormData({ ...formData, fuelCapacity: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      min={0}
                      step={0.1}
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingDevice(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingDevice ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingDevice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Supprimer le véhicule</h2>
              <p className="text-gray-600 mb-4">
                Êtes-vous sûr de vouloir supprimer le véhicule "{deletingDevice.name}" ?
                <br />
                <span className="text-red-600 text-sm">
                  Cette action supprimera également toutes les positions et alertes associées.
                </span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingDevice(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && transferringDevice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Transférer le véhicule</h2>
              <p className="text-gray-600 mb-4">
                Transférer "{transferringDevice.name}" de{' '}
                <strong>{transferringDevice.tenant.name}</strong> vers un autre tenant.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant cible
                </label>
                <select
                  value={transferTargetId}
                  onChange={(e) => setTransferTargetId(e.target.value)}
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                >
                  <option value="">Sélectionner un tenant...</option>
                  {tenants
                    .filter((t) => t.id !== transferringDevice.tenant.id)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setTransferringDevice(null);
                    setTransferTargetId('');
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={!transferTargetId}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                >
                  Transférer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setIsImportModalOpen(false)}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Importer des véhicules
                </h2>
                <button
                  onClick={() => setIsImportModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {!importResult ? (
                <>
                  {/* Tenant selector */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tenant cible
                    </label>
                    <select
                      value={importTenantId}
                      onChange={(e) => setImportTenantId(e.target.value)}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                    >
                      <option value="">Sélectionner un tenant...</option>
                      {tenants.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Upload zone */}
                  <div className="mb-6">
                    <label
                      htmlFor="csv-upload-admin"
                      className="flex flex-col items-center justify-center w-full h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-10 h-10 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                        </p>
                        <p className="text-xs text-gray-400">Fichier CSV uniquement</p>
                      </div>
                      <input
                        id="csv-upload-admin"
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              const text = event.target?.result as string;
                              const lines = text.split('\n');
                              const headers = lines[0].split(',').map((h) => h.trim());
                              const data = lines.slice(1)
                                .filter((line) => line.trim())
                                .map((line) => {
                                  const values = line.split(',');
                                  const obj: Record<string, string> = {};
                                  headers.forEach((header, index) => {
                                    obj[header] = values[index]?.trim() || '';
                                  });
                                  return obj;
                                });
                              setImportData(data);
                            };
                            reader.readAsText(file);
                          }
                        }}
                      />
                    </label>
                  </div>

                  {/* Download template */}
                  <div className="mb-6 flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Modèle CSV</p>
                        <p className="text-xs text-gray-500">Téléchargez le modèle avec les colonnes requises</p>
                      </div>
                    </div>
                    <a
                      href={adminApi.getImportTemplate()}
                      download
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-cyan-600 hover:bg-cyan-50 rounded-lg"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger
                    </a>
                  </div>

                  {/* Preview */}
                  {importData.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        Aperçu ({importData.length} véhicule{importData.length > 1 ? 's' : ''})
                      </h3>
                      <div className="max-h-48 overflow-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">IMEI</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Nom</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Plaque</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Marque</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {importData.slice(0, 5).map((row, i) => (
                              <tr key={i}>
                                <td className="px-3 py-2 text-sm text-gray-900">{row.imei}</td>
                                <td className="px-3 py-2 text-sm text-gray-900">{row.name}</td>
                                <td className="px-3 py-2 text-sm text-gray-500">{row.plate || '-'}</td>
                                <td className="px-3 py-2 text-sm text-gray-500">{row.brand || '-'}</td>
                              </tr>
                            ))}
                            {importData.length > 5 && (
                              <tr>
                                <td colSpan={4} className="px-3 py-2 text-center text-sm text-gray-500">
                                  ... et {importData.length - 5} autre(s)
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setIsImportModalOpen(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={importData.length === 0 || !importTenantId || importLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {importLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Import en cours...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Importer {importData.length} véhicule{importData.length > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                /* Results */
                <div className="text-center py-6">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${importResult.imported > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                    {importResult.imported > 0 ? (
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    ) : (
                      <XCircle className="h-8 w-8 text-red-600" />
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Import terminé
                  </h3>

                  <div className="flex justify-center gap-6 mb-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{importResult.imported}</p>
                      <p className="text-sm text-gray-500">Importés</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600">{importResult.skipped}</p>
                      <p className="text-sm text-gray-500">Ignorés</p>
                    </div>
                  </div>

                  {importResult.errors.length > 0 && (
                    <div className="max-h-40 overflow-auto mb-6 text-left">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Erreurs:</h4>
                      <ul className="space-y-1">
                        {importResult.errors.map((err, i) => (
                          <li key={i} className="text-sm text-red-600">
                            IMEI {err.imei}: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={() => setIsImportModalOpen(false)}
                    className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                  >
                    Fermer
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* SMS Modal */}
      {showSmsModal && smsDevice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Envoyer SMS Truphone</h3>
                  <p className="text-sm text-gray-500">{smsDevice.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowSmsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* ICCID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ICCID</label>
                <input
                  type="text"
                  value={smsDevice.simIccid || ''}
                  readOnly
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600"
                />
              </div>

              {/* Command Selection */}
              <TeltonikaCommandPicker
                value={smsCommand}
                onChange={handleSmsCommandChange}
              />

              {/* Value input for needsValue commands */}
              {selectedSmsCmd?.needsValue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valeur <span className="text-xs text-gray-400 font-normal">({selectedSmsCmd.valueHint})</span>
                  </label>
                  <input
                    type="text"
                    value={smsMessage.replace(selectedSmsCmd.value, '')}
                    onChange={(e) => setSmsMessage(selectedSmsCmd.value + e.target.value)}
                    placeholder={selectedSmsCmd.placeholder || ''}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  />
                </div>
              )}

              {/* SMS Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message SMS {smsCommand !== 'custom' && <span className="text-xs text-gray-400 font-normal">(auto-rempli)</span>}
                </label>
                <textarea
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  placeholder="Entrez la commande à envoyer..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Le message sera envoyé au format : <code className="bg-gray-100 px-1 rounded">&lt;identifiant&gt; &lt;mot de passe&gt; &lt;commande&gt;</code>
                </p>
              </div>

              {/* Result */}
              {smsResult && (
                <div className={clsx(
                  'p-3 rounded-lg flex items-center gap-2 text-sm',
                  smsResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                )}>
                  {smsResult.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                  {smsResult.text}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowSmsModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Fermer
              </button>
              <button
                onClick={handleSendSms}
                disabled={smsLoading || !smsMessage}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {smsLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Envoyer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Suppression groupée</h2>
              <p className="text-gray-600 mb-2">
                Êtes-vous sûr de vouloir supprimer <strong>{selectedDevices.size}</strong> véhicule{selectedDevices.size > 1 ? 's' : ''} ?
              </p>
              <p className="text-red-600 text-sm mb-4">
                Cette action supprimera également toutes les positions, alertes, trajets et SMS associés. Cette action est irréversible.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={bulkDeleteLoading}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {bulkDeleteLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Supprimer ({selectedDevices.size})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk SMS Modal */}
      {showBulkSmsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">SMS groupé</h3>
                  <p className="text-sm text-gray-500">{selectedDevices.size} véhicule{selectedDevices.size > 1 ? 's' : ''} sélectionné{selectedDevices.size > 1 ? 's' : ''}</p>
                </div>
              </div>
              <button onClick={() => setShowBulkSmsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">
                La commande sera envoyée à tous les véhicules sélectionnés ayant une SIM Truphone.
              </p>

              {/* Command Selection */}
              <TeltonikaCommandPicker
                value={bulkSmsCommand}
                onChange={handleBulkSmsCommandChange}
              />

              {/* Value input for needsValue commands */}
              {selectedBulkSmsCmd?.needsValue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valeur <span className="text-xs text-gray-400 font-normal">({selectedBulkSmsCmd.valueHint})</span>
                  </label>
                  <input
                    type="text"
                    value={bulkSmsMessage.replace(selectedBulkSmsCmd.value, '')}
                    onChange={(e) => setBulkSmsMessage(selectedBulkSmsCmd.value + e.target.value)}
                    placeholder={selectedBulkSmsCmd.placeholder || ''}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  />
                </div>
              )}

              {/* SMS Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message SMS {bulkSmsCommand !== 'custom' && <span className="text-xs text-gray-400 font-normal">(auto-rempli)</span>}
                </label>
                <textarea
                  value={bulkSmsMessage}
                  onChange={(e) => setBulkSmsMessage(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  placeholder="Entrez la commande à envoyer..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Le message sera envoyé au format : <code className="bg-gray-100 px-1 rounded">&lt;identifiant&gt; &lt;mot de passe&gt; &lt;commande&gt;</code>
                </p>
              </div>

              {/* Result */}
              {bulkSmsResult && (
                <div className={clsx(
                  'p-3 rounded-lg flex items-center gap-2 text-sm',
                  bulkSmsResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                )}>
                  {bulkSmsResult.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                  {bulkSmsResult.text}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowBulkSmsModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Fermer
              </button>
              <button
                onClick={handleBulkSms}
                disabled={bulkSmsLoading || !bulkSmsMessage}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkSmsLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Envoyer à {selectedDevices.size} véhicule{selectedDevices.size > 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FOTA Web Push Config Modal */}
      {showFotaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                  <Upload className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">FOTA Web - Push Configuration</h3>
                  <p className="text-sm text-gray-500">{selectedDevices.size} véhicule{selectedDevices.size > 1 ? 's' : ''} sélectionné{selectedDevices.size > 1 ? 's' : ''}</p>
                </div>
              </div>
              <button onClick={() => setShowFotaModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">
                Envoyez un fichier de configuration (.cfg) aux trackers Teltonika via FOTA Web.
                Les trackers recevront la config à leur prochain check-in.
              </p>

              {/* IMEIs preview */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-500 mb-1">IMEIs ciblés :</p>
                <p className="text-sm text-gray-700 font-mono break-all">
                  {Array.from(selectedDevices)
                    .map((id) => devices.find((d) => d.id === id))
                    .filter(Boolean)
                    .map((d) => d!.imei)
                    .join(', ')}
                </p>
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier de configuration (.cfg)
                </label>
                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-violet-400 hover:bg-violet-50 transition-colors">
                  <input
                    type="file"
                    accept=".cfg"
                    onChange={handleFotaCfgSelect}
                    className="hidden"
                  />
                  {fotaCfgFile ? (
                    <div className="flex items-center gap-2 text-violet-700">
                      <FileText className="h-5 w-5" />
                      <span className="text-sm font-medium">{fotaCfgFile.name}</span>
                      <span className="text-xs text-gray-400">({(fotaCfgFile.size / 1024).toFixed(1)} Ko)</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Upload className="h-5 w-5" />
                      <span className="text-sm">Cliquez pour sélectionner un fichier .cfg</span>
                    </div>
                  )}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Généré via Teltonika Configurator (logiciel desktop)
                </p>
              </div>

              {/* Info */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    La configuration ne sera pas appliquée instantanément. Chaque tracker la récupérera
                    lors de son prochain check-in au serveur FOTA (de quelques minutes à quelques heures).
                  </p>
                </div>
              </div>

              {/* Result */}
              {fotaResult && (
                <div className={clsx(
                  'p-3 rounded-lg flex items-center gap-2 text-sm',
                  fotaResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                )}>
                  {fotaResult.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {fotaResult.text}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowFotaModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Fermer
              </button>
              <button
                onClick={handleFotaPush}
                disabled={fotaLoading || !fotaCfgBase64}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {fotaLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Envoyer à {selectedDevices.size} véhicule{selectedDevices.size > 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Transfer Modal */}
      {showBulkTransferModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => !bulkTransferLoading && setShowBulkTransferModal(false)} />
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <ArrowRightLeft className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Transfert groupé</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Transférer <strong>{selectedDevices.size}</strong> véhicule{selectedDevices.size > 1 ? 's' : ''} vers un autre tenant.
                Les groupes et conducteurs associés seront réinitialisés.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant cible</label>
                <select
                  value={bulkTransferTenantId}
                  onChange={(e) => setBulkTransferTenantId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">Sélectionner un tenant...</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkTransferModal(false)}
                  disabled={bulkTransferLoading}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleBulkTransfer}
                  disabled={bulkTransferLoading || !bulkTransferTenantId}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {bulkTransferLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRightLeft className="h-4 w-4" />
                  )}
                  Transférer ({selectedDevices.size})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Truphone Import Modal */}
      {showTruphoneImportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => truphoneImportStep !== 'sending' && truphoneImportStep !== 'collecting' && truphoneImportStep !== 'creating' && setShowTruphoneImportModal(false)} />
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl relative z-10">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                    <Radio className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Import Truphone</h3>
                    <p className="text-sm text-gray-500">
                      {truphoneImportStep === 'config' && 'Sélectionnez le tenant cible'}
                      {truphoneImportStep === 'sending' && 'Envoi en cours...'}
                      {truphoneImportStep === 'waiting' && 'En attente des réponses'}
                      {truphoneImportStep === 'collecting' && 'Collecte en cours...'}
                      {truphoneImportStep === 'results' && 'Résultats du scan'}
                      {truphoneImportStep === 'creating' && 'Création en cours...'}
                      {truphoneImportStep === 'done' && 'Import terminé'}
                    </p>
                  </div>
                </div>
                {truphoneImportStep !== 'sending' && truphoneImportStep !== 'collecting' && truphoneImportStep !== 'creating' && (
                  <button onClick={() => setShowTruphoneImportModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="p-6">
                {/* Step 1: Config */}
                {truphoneImportStep === 'config' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tenant cible</label>
                      <select
                        value={truphoneImportTenantId}
                        onChange={(e) => setTruphoneImportTenantId(e.target.value)}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500"
                      >
                        <option value="">Sélectionner un tenant...</option>
                        {tenants.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={handleTruphoneSend}
                        disabled={!truphoneImportTenantId}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                      >
                        <Send className="h-4 w-4" />
                        Envoyer getver aux SIMs
                      </button>
                      <button
                        onClick={handleTruphoneCollect}
                        disabled={!truphoneImportTenantId}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                        title="Récupérer directement les réponses déjà reçues dans l'historique SMS"
                      >
                        <Download className="h-4 w-4" />
                        Récupérer les réponses existantes
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2a: Sending */}
                {truphoneImportStep === 'sending' && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin text-cyan-500" />
                    <p className="text-gray-600 text-center">
                      Envoi de <code>getver</code> aux SIMs actives...
                    </p>
                  </div>
                )}

                {/* Step 2b: Waiting for responses */}
                {truphoneImportStep === 'waiting' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                      <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-green-800">
                          getver envoyé à {truphoneSendResult?.sent || 0} SIM{(truphoneSendResult?.sent || 0) > 1 ? 's' : ''} sur {truphoneSendResult?.totalActive || 0} actives
                        </p>
                        <p className="text-sm text-green-700">
                          {(truphoneSendResult?.skipped || 0) > 0 && (
                            <>{truphoneSendResult?.skipped} SIM{(truphoneSendResult?.skipped || 0) > 1 ? 's' : ''} ignorée{(truphoneSendResult?.skipped || 0) > 1 ? 's' : ''} (déjà associées à un véhicule). </>
                          )}
                          Les réponses arrivent dans l'historique SMS. Attendez quelques secondes puis cliquez ci-dessous.
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleTruphoneCollect}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                      >
                        <Download className="h-4 w-4" />
                        Récupérer les réponses
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2c: Collecting */}
                {truphoneImportStep === 'collecting' && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin text-cyan-500" />
                    <p className="text-gray-600 text-center">
                      Lecture de l'historique SMS et analyse des réponses...
                    </p>
                  </div>
                )}

                {/* Step 3: Results */}
                {truphoneImportStep === 'results' && truphoneScanResult && (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="flex gap-4 text-sm">
                      <span className="px-3 py-1 bg-gray-100 rounded-full">
                        {truphoneScanResult.totalActive} SIMs actives
                      </span>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full">
                        {truphoneScanResult.newDevices.length + truphoneScanResult.existingDevices.length} réponses
                      </span>
                      <span className="px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full">
                        {truphoneImportDevices.filter((d) => d.selected).length} nouveaux à importer
                      </span>
                    </div>

                    {/* New devices table */}
                    {truphoneImportDevices.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Nouveaux véhicules ({truphoneImportDevices.length})</h4>
                        <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-2 py-2 w-8">
                                  <input
                                    type="checkbox"
                                    checked={truphoneImportDevices.every((d) => d.selected)}
                                    onChange={() => {
                                      const allSelected = truphoneImportDevices.every((d) => d.selected);
                                      setTruphoneImportDevices((prev) =>
                                        prev.map((d) => ({ ...d, selected: !allSelected }))
                                      );
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 text-cyan-600"
                                  />
                                </th>
                                <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">ICCID</th>
                                <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">IMEI</th>
                                <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">Modèle</th>
                                <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">Tag</th>
                                <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">Nom</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {truphoneImportDevices.map((d, idx) => (
                                <tr key={d.iccid} className={d.selected ? 'bg-cyan-50/50' : ''}>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="checkbox"
                                      checked={d.selected}
                                      onChange={() =>
                                        setTruphoneImportDevices((prev) =>
                                          prev.map((item, i) => i === idx ? { ...item, selected: !item.selected } : item)
                                        )
                                      }
                                      className="h-4 w-4 rounded border-gray-300 text-cyan-600"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <code className="text-xs bg-gray-100 px-1 rounded">{d.iccid.slice(-8)}</code>
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <code className="text-xs">{d.imei}</code>
                                  </td>
                                  <td className="px-2 py-1.5 text-gray-600">{d.trackerModel || '-'}</td>
                                  <td className="px-2 py-1.5">
                                    {d.tag ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {d.tag}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={d.name}
                                      onChange={(e) =>
                                        setTruphoneImportDevices((prev) =>
                                          prev.map((item, i) => i === idx ? { ...item, name: e.target.value } : item)
                                        )
                                      }
                                      className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:border-cyan-500 focus:outline-none"
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Existing devices (collapsible) */}
                    {truphoneScanResult.existingDevices.length > 0 && (
                      <div>
                        <button
                          onClick={() => setTruphoneShowExisting(!truphoneShowExisting)}
                          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                        >
                          {truphoneShowExisting ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          Déjà existants ({truphoneScanResult.existingDevices.length})
                        </button>
                        {truphoneShowExisting && (
                          <div className="mt-2 border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">ICCID</th>
                                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">IMEI</th>
                                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Véhicule</th>
                                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Tenant</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {truphoneScanResult.existingDevices.map((d) => (
                                  <tr key={d.iccid}>
                                    <td className="px-3 py-1.5"><code className="text-xs">{d.iccid.slice(-8)}</code></td>
                                    <td className="px-3 py-1.5"><code className="text-xs">{d.imei}</code></td>
                                    <td className="px-3 py-1.5 text-gray-600">{d.deviceName}</td>
                                    <td className="px-3 py-1.5 text-gray-600">{d.tenantName}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No response (collapsible) */}
                    {truphoneScanResult.noResponse.length > 0 && (
                      <div>
                        <button
                          onClick={() => setTruphoneShowNoResponse(!truphoneShowNoResponse)}
                          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                        >
                          {truphoneShowNoResponse ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          Sans réponse ({truphoneScanResult.noResponse.length})
                        </button>
                        {truphoneShowNoResponse && (
                          <div className="mt-2 border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">ICCID</th>
                                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">MSISDN</th>
                                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Label</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {truphoneScanResult.noResponse.map((d) => (
                                  <tr key={d.iccid}>
                                    <td className="px-3 py-1.5"><code className="text-xs">{d.iccid.slice(-8)}</code></td>
                                    <td className="px-3 py-1.5 text-gray-600">{d.msisdn || '-'}</td>
                                    <td className="px-3 py-1.5 text-gray-600">{d.simLabel || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex justify-between pt-2">
                      <button
                        onClick={handleTruphoneCollect}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        title="Re-collecter les réponses (si de nouvelles sont arrivées)"
                      >
                        <Download className="h-4 w-4" />
                        Actualiser les réponses
                      </button>
                      <button
                        onClick={handleTruphoneImportCreate}
                        disabled={truphoneImportDevices.filter((d) => d.selected).length === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4" />
                        Importer {truphoneImportDevices.filter((d) => d.selected).length} véhicule{truphoneImportDevices.filter((d) => d.selected).length > 1 ? 's' : ''}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Creating */}
                {truphoneImportStep === 'creating' && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin text-cyan-500" />
                    <p className="text-gray-600">Création des véhicules en cours...</p>
                  </div>
                )}

                {/* Step 5: Done */}
                {truphoneImportStep === 'done' && truphoneImportResult && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">
                          {truphoneImportResult.created} véhicule{truphoneImportResult.created > 1 ? 's' : ''} créé{truphoneImportResult.created > 1 ? 's' : ''}
                        </p>
                        {truphoneImportResult.skipped > 0 && (
                          <p className="text-sm text-green-700">{truphoneImportResult.skipped} ignoré{truphoneImportResult.skipped > 1 ? 's' : ''} (déjà existants)</p>
                        )}
                      </div>
                    </div>
                    {truphoneImportResult.errors.length > 0 && (
                      <div className="p-4 bg-red-50 rounded-lg">
                        <p className="font-medium text-red-800 mb-2">Erreurs :</p>
                        <ul className="text-sm text-red-700 space-y-1">
                          {truphoneImportResult.errors.map((e, i) => (
                            <li key={i}>{e.imei}: {e.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="flex justify-end">
                      <button
                        onClick={() => {
                          setShowTruphoneImportModal(false);
                          loadDevices();
                        }}
                        className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
